import React, { useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select2";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { deletebutton } from "@/constants";
import { BackArrowIcon } from "@/assets/icons/admin";
import { useUpdatePartnership } from "@/services/admin.service";
import { toast } from "react-toastify";
import { PartnershipData, PartnershipUpdateData } from "@/types";
import PhoneInput from "react-phone-input-2";

interface EditAccountProps {
  onCancel: () => void;
  partnershipId: string;
  partnership?: PartnershipData;
}

const platforms = [
  "YouTube",
  "Twitch",
  "Instagram",
  "Twitter",
  "Facebook",
] as const;

const formSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  telegramOrWhatsapp: z.string().optional(),
  primaryPlatform: z.enum(platforms).optional(),
  linkPrimaryPlatform: z.string().optional(),
  followersPrimaryPlatform: z.string().optional(),
  secondaryPlatform: z.enum(platforms).optional(),
  linkSecondaryPlatform: z.string().url().optional(),
  followersSecondaryPlatform: z.string().optional(),
  otherPlatforms: z
    .array(
      z.object({
        platform: z.enum(platforms).optional(),
        link: z.string().url().optional(),
        followers: z.string().optional(),
      }),
    )
    .optional(),
  linkOtherPlatform: z.string().url().optional(),
  followersOtherPlatform: z.string().optional(),
  averageViews: z.string().optional(),
  hasExperience: z.boolean(),
  audienceInterest: z.string().optional(),
  motivation: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const EditApplicationForm: React.FC<EditAccountProps> = ({
  onCancel,
  partnershipId,
  partnership,
}) => {
  const [otherPlatforms, setOtherPlatforms] = useState<
    { platform: string; link: string; followers: string }[]
  >([]);

  const primaryPlatform = partnership?.platforms[0];
  const secondaryPlatform = partnership?.platforms[1];
  const additionalPlatforms = partnership?.platforms.slice(2) || [];

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: partnership?.name,
      email: partnership?.email,
      telegramOrWhatsapp: partnership?.contactNo,
      primaryPlatform:
        primaryPlatform?.platformName as FormData["primaryPlatform"],
      linkPrimaryPlatform: primaryPlatform?.link,
      followersPrimaryPlatform: primaryPlatform?.followerCount,
      secondaryPlatform:
        secondaryPlatform?.platformName as FormData["secondaryPlatform"],
      linkSecondaryPlatform: secondaryPlatform?.link,
      followersSecondaryPlatform: secondaryPlatform?.followerCount,
      otherPlatforms: additionalPlatforms.map((platform) => ({
        platform: platform.platformName as FormData["primaryPlatform"],
        link: platform.link,
        followers: platform.followerCount,
      })),
      averageViews: partnership?.engagement,
      hasExperience: partnership?.isExpereiencedInCrypto || false,
      audienceInterest: partnership?.audianceInterest,
      motivation: partnership?.motivation,
    },
  });

  const updatePartnership = useUpdatePartnership(); // Use the mutation hook

  const onSubmit: SubmitHandler<FormData> = (values) => {
    const updatedData: PartnershipUpdateData = {
      name: values.name,
      email: values.email,
      contactNo: values.telegramOrWhatsapp || "",
      platforms: [
        ...(values.primaryPlatform
          ? [
              {
                platformName: values.primaryPlatform,
                link: values.linkPrimaryPlatform || "",
                followerCount: values.followersPrimaryPlatform || "0",
              },
            ]
          : []),
        ...(values.secondaryPlatform
          ? [
              {
                platformName: values.secondaryPlatform,
                link: values.linkSecondaryPlatform || "",
                followerCount: values.followersSecondaryPlatform || "0",
              },
            ]
          : []),
        ...(values.otherPlatforms?.map((platform) => ({
          platformName: platform.platform || "",
          link: platform.link || "",
          followerCount: platform.followers || "0",
        })) || []),
      ],
      engagement: values.averageViews || "",
      rejectionNote: "",
      isExpereiencedInCrypto: values.hasExperience,
      audianceInterest: values.audienceInterest || "",
      motivation: values.motivation || "",
    };

    // Use the mutation function to update the partnership
    updatePartnership.mutate(
      { id: partnershipId, data: updatedData },
      {
        onSuccess: () => {
          toast.success("Partnership updated successfully.");
          onCancel();
        },
        onError: (error: any) => {
          toast.error("Error updating partnership: " + error.message);
        },
      },
    );
  };

  console.log("partnership data .....", partnership);

  const handleAddPlatform = () => {
    setOtherPlatforms([
      ...otherPlatforms,
      { platform: "", link: "", followers: "" },
    ]);
  };

  const handleRemovePlatform = (index: number) => {
    const updatedPlatforms = otherPlatforms.filter((_, i) => i !== index);
    setOtherPlatforms(updatedPlatforms);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6 rounded-lg px-2 py-6 font-kanit text-white sm:px-6"
      >
        <div className="font-ubuntu mb-4 flex w-full items-center gap-3 text-2xl font-semibold text-white">
          <button onClick={onCancel} className="">
            <BackArrowIcon />
          </button>
          <div className="text-xl font-bold">Edit Partnership</div>
        </div>

        <div className="space-y-4">
          <h2 className="font-semibold">Personal Information</h2>
          {/* Name */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/50">Your Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Type Here"
                    {...field}
                    className="h-[50px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/50">Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Type Here"
                      {...field}
                      className="h-[50px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Telegram or WhatsApp */}
            <FormField
              control={form.control}
              name="telegramOrWhatsapp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/50">
                    Telegram or WhatsApp
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-center rounded-md border border-white/10 bg-transparent">
                      <Controller
                        name="telegramOrWhatsapp"
                        control={form.control}
                        render={({ field }) => (
                          <PhoneInput
                            country={"us"} // Set the default country
                            value={field.value}
                            onChange={field.onChange}
                            inputStyle={{
                              backgroundColor: "transparent",
                              border: "none",
                              color: "#ffffff",
                              width: "100%",
                              height: "49px",
                            }}
                            buttonStyle={{
                              backgroundColor: "transparent",
                              borderColor: "transparent",
                            }}
                            dropdownStyle={{
                              backgroundColor: "#1A1E33",
                              color: "#ffffff",
                            }}
                            containerStyle={{
                              flex: 1,
                              display: "flex",
                            }}
                          />
                        )}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h2 className="font-semibold">
            Social Media and Platform Information
          </h2>
          {/* Primary Platform */}
          <FormField
            control={form.control}
            name="primaryPlatform"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/50">
                  Primary Platform
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-[50px] ">
                      <SelectValue placeholder="Select a platform" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {platforms.map((platform) => (
                      <SelectItem key={platform} value={platform}>
                        {platform}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-3 border-b border-white/65 pb-4 sm:grid-cols-2">
            {/* Link Primary Platform */}
            <FormField
              control={form.control}
              name="linkPrimaryPlatform"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/50">
                    Link Primary Platform
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Type Here"
                      {...field}
                      className="h-[50px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Number of Followers Primary Platform */}
            <FormField
              control={form.control}
              name="followersPrimaryPlatform"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/50">
                    Number of Followers Primary Platform
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Type Here"
                      {...field}
                      className="h-[50px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Secondary Platform */}
          <FormField
            control={form.control}
            name="secondaryPlatform"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/50">
                  Secondary Platform (Optional)
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-[50px] ">
                      <SelectValue placeholder="Select a platform" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {platforms.map((platform: any) => (
                      <SelectItem key={platform} value={platform}>
                        {platform}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            {/* Link Secondary Platform */}
            <FormField
              control={form.control}
              name="linkSecondaryPlatform"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/50">
                    Link Platform Secondary Platform (Optional)
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Type Here"
                      {...field}
                      className="h-[50px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Number of Followers Secondary Platform */}
            <FormField
              control={form.control}
              name="followersSecondaryPlatform"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/50">
                    Number of Followers Secondary Platform (Optional)
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Type Here"
                      {...field}
                      className="h-[50px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Additional Platforms Section */}
          {otherPlatforms.map((_, index) => (
            <div
              key={index}
              className="space-y-4 border-t border-white/65 pt-4"
            >
              {/* Platform */}
              <FormField
                control={form.control}
                name={`otherPlatforms.${index}.platform`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white/50">
                      Other Platform (Optional)
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-[50px] ">
                          <SelectValue placeholder="Select a platform" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {platforms.map((platform) => (
                          <SelectItem key={platform} value={platform}>
                            {platform}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                {/* Link */}
                <FormField
                  control={form.control}
                  name={`otherPlatforms.${index}.link`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/50">
                        Link Platform Other Platform (Optional)
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Type Here"
                          {...field}
                          className="h-[50px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Followers */}
                <FormField
                  control={form.control}
                  name={`otherPlatforms.${index}.followers`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/50">
                        Number of Followers Other Platform (Optional)
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Type Here"
                          {...field}
                          className="h-[50px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemovePlatform(index)}
                className="flex h-[52px] w-full items-center justify-center gap-2 text-[#FF756C]"
              >
                <img src={deletebutton} alt="deletebutton" />
                <div> Delete Platform</div>
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={handleAddPlatform}
            className="h-[52px] w-full rounded-lg bg-[#00154A] text-white/65"
          >
            + Add More Platform
          </button>
          {/* Average Monthly Views */}
          <FormField
            control={form.control}
            name="averageViews"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/50">
                  Average Monthly Views or Engagement
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Type Here"
                    {...field}
                    className="h-[50px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <h2 className="font-semibold">
            Gaming and Cryptocurrency Affiliation
          </h2>
          {/* Experience with Online Casinos or Cryptocurrency */}
          <FormField
            control={form.control}
            name="hasExperience"
            render={({ field }) => (
              <FormItem>
                <FormLabel className=" text-wrap text-white/50">
                  Do you have experience with online casinos or cryptocurrency?
                </FormLabel>
                <div className="mb-1 flex items-center gap-2">
                  <label
                    className={`cursor-pointer  rounded-lg p-[1px] max-sm:flex-grow sm:w-[124px] ${
                      field.value === true
                        ? "bg-gradient-to-r from-[#003BD2] to-[#00FFC2] text-white"
                        : "bg-transparent"
                    }`}
                  >
                    <input
                      type="radio"
                      value="yes"
                      checked={field.value === true}
                      onChange={() => field.onChange(true)}
                      className="sr-only"
                    />
                    <div
                      className={`flex h-[54px] items-center justify-center gap-2 py-2 text-center sm:w-[122px] ${
                        field.value === true ? "bg-[#2b2b3f] text-white" : ""
                      } rounded-lg px-4 text-sm font-normal  sm:font-bold`}
                    >
                      <div
                        className={`h-2 w-2 rounded-full  outline outline-2 outline-offset-2  ${field.value === true ? "bg-[#1E83ED] outline-[#1E83ED]" : "bg-transparent outline-[#637381]"}`}
                      ></div>{" "}
                      <div className="font-ubuntuMono text-sm font-bold">
                        Yes
                      </div>
                    </div>
                  </label>
                  <label
                    className={`cursor-pointer rounded-lg p-[1px] max-sm:flex-grow sm:w-[124px] ${
                      field.value === false
                        ? "bg-gradient-to-r from-[#003BD2] to-[#00FFC2] text-white"
                        : "bg-transparent"
                    }`}
                  >
                    <input
                      type="radio"
                      value="no"
                      checked={field.value === false}
                      onChange={() => field.onChange(false)}
                      className="sr-only"
                    />
                    <div
                      className={`flex h-[54px] items-center justify-center gap-2 py-2 text-center sm:w-[122px] ${
                        field.value === false ? "bg-[#2b2b3f] text-white" : ""
                      }  rounded-lg px-4 text-sm font-normal sm:font-bold`}
                    >
                      <div
                        className={`h-2 w-2 rounded-full  outline outline-2 outline-offset-2  ${field.value === false ? "bg-[#1E83ED] outline-[#1E83ED]" : "bg-transparent outline-[#637381]"}`}
                      ></div>{" "}
                      <div className="font-ubuntuMono text-sm font-bold">
                        No
                      </div>
                    </div>
                  </label>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Audience Interest */}
          <FormField
            control={form.control}
            name="audienceInterest"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="text-wrap text-white/50">
                  Please describe your audience's interest in gaming and/or
                  cryptocurrency
                </FormLabel>
                <FormControl>
                  <textarea
                    rows={3}
                    placeholder="Type Here"
                    {...field}
                    className="h-28 w-full resize-none rounded-md border border-white/5 bg-transparent p-2 font-kanit text-sm text-white placeholder:p-1 placeholder:text-white/30"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <h2 className="font-semibold">Motivation and Strategy</h2>
          {/* Motivation */}
          <FormField
            control={form.control}
            name="motivation"
            render={({ field }) => (
              <FormItem className="flex flex-col text-wrap">
                <label className="text-white/50">
                  Why do you want to join the Sharkroll Casino Affiliate
                  Program?
                </label>
                <FormControl className="w-full">
                  <textarea
                    rows={3}
                    placeholder="Type Here"
                    {...field}
                    className="h-24 w-full resize-none rounded-md border border-white/5 bg-transparent p-2 text-sm text-white placeholder:p-1 placeholder:text-white/30"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            className="mt-1 h-[36px] w-full bg-[#2998FF] font-ubuntuMono  text-[15px] font-normal sm:w-[140px]"
          >
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default EditApplicationForm;
